/**
 * Live CAD paint rules for Command Center location (lock 2026-08-19).
 *
 * Renderer-agnostic: Cesium (and any later adapter) consumes these numbers.
 * Do not reimplement fade, hide, halo, Unassigned, or the trail cap inside a map engine.
 */

export const LIVE_HIDE_INTERVAL_MULTIPLIER = 4;
export const LIVE_TRAIL_PAINT_CAP = 12;
export const UNASSIGNED_SITE_GROUP = "Unassigned";

/** Opacity when a kept-after-hide dot must still be findable (selected / filtered / Focus). */
export const LIVE_DOT_FLOOR_OPACITY = 0.28;

export interface LiveDotPaintInput {
  ageSeconds: number | null;
  intervalSeconds: number;
  selected?: boolean;
  filtered?: boolean;
  focused?: boolean;
  dutyCallId?: string | null;
}

export interface LiveDotPaint {
  paint: boolean;
  opacity: number;
  onCallHalo: boolean;
}

export function liveHideAfterSeconds(intervalSeconds: number): number {
  const safeInterval = intervalSeconds > 0 ? intervalSeconds : 300;
  return safeInterval * LIVE_HIDE_INTERVAL_MULTIPLIER;
}

export function hasOnCallHalo(dutyCallId: string | null | undefined): boolean {
  return typeof dutyCallId === "string" && dutyCallId.length > 0;
}

/**
 * Site cluster key. Empty / missing site names are Unassigned.
 * Never invent a parcel; callers still plot the ping at its true coordinates.
 */
export function liveSiteGroupKey(siteName: string | null | undefined): string {
  if (typeof siteName !== "string") {
    return UNASSIGNED_SITE_GROUP;
  }
  const trimmed = siteName.trim();
  return trimmed.length === 0 ? UNASSIGNED_SITE_GROUP : trimmed;
}

export function isUnassignedSiteGroup(siteName: string | null | undefined): boolean {
  return liveSiteGroupKey(siteName) === UNASSIGNED_SITE_GROUP;
}

function keepVisibleAfterHide(input: LiveDotPaintInput): boolean {
  return input.selected === true || input.filtered === true || input.focused === true;
}

/**
 * Linear fade from 1 at age 0 to LIVE_DOT_FLOOR_OPACITY at the 4x hide threshold.
 * Missing age does not paint (there is no latest dot to show).
 */
export function liveDotPaint(input: LiveDotPaintInput): LiveDotPaint {
  const onCallHalo = hasOnCallHalo(input.dutyCallId ?? null);
  const hideAfter = liveHideAfterSeconds(input.intervalSeconds);
  if (input.ageSeconds === null) {
    return { paint: false, opacity: 0, onCallHalo };
  }
  const age = Math.max(0, input.ageSeconds);
  if (age > hideAfter && !keepVisibleAfterHide(input)) {
    return { paint: false, opacity: 0, onCallHalo };
  }
  const t = Math.min(age / hideAfter, 1);
  const opacity = 1 - t * (1 - LIVE_DOT_FLOOR_OPACITY);
  return { paint: true, opacity, onCallHalo };
}

export interface LiveTrailCandidate {
  id: string;
  rank: number;
}

/**
 * Live paints at most 12 full trails. Rank is caller-supplied (lower is kept).
 * Remainder ids are honest leftovers for the list, never silently dropped.
 */
export function capLiveTrails(candidates: readonly LiveTrailCandidate[]): {
  drawnIds: string[];
  remainderIds: string[];
} {
  const sorted = [...candidates].sort((a, b) => a.rank - b.rank || a.id.localeCompare(b.id));
  return {
    drawnIds: sorted.slice(0, LIVE_TRAIL_PAINT_CAP).map((row) => row.id),
    remainderIds: sorted.slice(LIVE_TRAIL_PAINT_CAP).map((row) => row.id),
  };
}
